/**
 * TerminalDetails Component
 *
 * This component displays detailed information about a specific terminal and allows
 * authorized users to edit the terminal's details. It fetches data from an API,
 * handles user input, and updates the terminal details via the API.
 */
import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { container } from "../../infrastructure/container"
import { SessionService } from "../../infrastructure/services/session"
import { Logger } from "../../infrastructure/services/logger"
import { ApiService } from "../../infrastructure/services/api"
import { ArrowLeft } from "lucide-react"
import { TYPES } from "../models/types"
import { COLORS } from "../constants/colors"
import { Status } from "../../infrastructure/models/enums/status"
import { Terminal } from "models/terminal"
import { Duration } from "../../infrastructure/models/enums/duration" // Import the Duration enum
import { FranchiseRole } from "../../infrastructure/models/enums/franchiserole"

const TerminalDetails = () => {
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")
  const sessionService = container.resolve<SessionService>("sessionService")

  const { terminalId } = useParams<{ terminalId: string }>()
  const [terminalDetails, setTerminalDetails] = useState<Terminal | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [editedFields, setEditedFields] = useState<Partial<Terminal>>({})
  const [alertMessage, setAlertMessage] = useState<string | null>(null)
  const [userRole] = useState<number | null>(sessionService?.user?.role ?? null)
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle")
  const navigate = useNavigate()

  const canEdit =
    userRole === FranchiseRole.OWNER ||
    userRole === FranchiseRole.ADMIN ||
    userRole === FranchiseRole.MANAGER
  const isOwnerOrAdmin = userRole === FranchiseRole.OWNER || userRole === FranchiseRole.ADMIN
  const isManager = userRole === FranchiseRole.MANAGER

  /** Fetch terminal details using apiService */
  useEffect(() => {
    const fetchTerminalDetails = async () => {
      setLoading(true)
      try {
        if (!terminalId) throw new Error("Invalid terminal ID.")
        logger.info("Fetching terminal details for:", terminalId)

        const response = await apiService.getTerminalDetails(terminalId)
        logger.info("API Response:", response)

        if (response) {
          setTerminalDetails(response)
        } else {
          throw new Error("No terminal details found.")
        }
      } catch (err) {
        logger.error("Error fetching terminal details:", err)
        setError(err instanceof Error ? err.message : "Error fetching details")
      } finally {
        setLoading(false)
      }
    }

    fetchTerminalDetails()
  }, [terminalId, apiService, logger])

  /** Toggles edit mode and resets edited fields */
  const handleEditToggle = () => {
    setEditMode((prev) => !prev)
    setEditedFields({})
  }

  /** Handles input changes for terminal details */
  const handleChange = (field: keyof Terminal, value: any) => {
    setEditedFields((prev) => ({
      ...prev,
      [field]: value === "" ? "" : value,
    }))
  }

  const handleNestedChange = (
    parentField: keyof Terminal,
    field: string,
    value: any,
    index?: number
  ) => {
    setEditedFields((prev) => {
      // Preserve the existing lockers array
      const lockers = prev.lockers || terminalDetails?.lockers || []

      // Update the nested field
      const parentValue = prev[parentField] as Record<string, any> | undefined
      const updatedParent =
        parentValue &&
        typeof parentValue === TYPES.OBJECT &&
        !Array.isArray(parentValue)
          ? { ...parentValue }
          : {}

      if (index !== undefined) {
        const arrayField = Array.isArray(updatedParent[field])
          ? [...updatedParent[field]]
          : []
        arrayField[index] = value
        updatedParent[field] = arrayField
      } else {
        updatedParent[field] = value
      }

      // Return the updated state with lockers preserved
      return {
        ...prev,
        [parentField]: updatedParent,
        lockers,
      }
    })
  }

  const handleClose = () => {
    navigate(-1)
  }

  setTimeout(() => {
    setSaveStatus("idle")
  }, 3000)

  /** Handles changes for locker status */
  const handleLockerStatusChange = (lockerIndex: number, status: string) => {
    setEditedFields((prev) => {
      const updatedLockers = [
        ...(prev.lockers || terminalDetails?.lockers || []),
      ]
      updatedLockers[lockerIndex] = {
        ...updatedLockers[lockerIndex],
        status,
      }
      return {
        ...prev,
        lockers: updatedLockers,
      }
    })
  }

  /** Saves edited changes using apiService.updateTerminalDetails */
  const handleSaveChanges = async () => {
    if (Object.keys(editedFields).length === 0) {
      setError("No changes to save.")
      return
    }

    if (!terminalId) {
      setError("Invalid terminal ID.")
      return
    }

    const role = sessionService.user?.role
    if (role === null || role === undefined) {
      setError("User role is missing. Please log in again.")
      return
    }

    // block non-authorized roles entirely
    const allowed =
      role === FranchiseRole.OWNER || role === FranchiseRole.ADMIN || role === FranchiseRole.MANAGER
    if (!allowed) {
      setError("You do not have permission to edit terminal details.")
      setSaveStatus("error")
      return
    }

    setSaveStatus("saving")

    try {
      const updatedLockers = editedFields.lockers
        ? editedFields.lockers.map((locker) => ({
            ...locker,
            terminal: terminalId,
          }))
        : []

      // Build initial payload from edited fields
      const payload: any = {
        terminalId,
        ...editedFields,
        lockers: editedFields.lockers ? updatedLockers : undefined,
      }

      // Preserve price arrays, only update edited price values
      if (editedFields.basePrice) {
        payload.basePrice = {
          ...terminalDetails?.basePrice,
          ...editedFields.basePrice,
          prices:
            terminalDetails?.basePrice?.prices?.map((price, index) => {
              const editedPrice = editedFields.basePrice?.prices?.[index]
              return editedPrice
                ? { ...price, price: editedPrice.price }
                : price
            }) || [],
        }
      }

      if (editedFields.additionalPrices) {
        payload.additionalPrices = {
          ...terminalDetails?.additionalPrices,
          ...editedFields.additionalPrices,
          prices:
            terminalDetails?.additionalPrices?.prices?.map((price, index) => {
              const editedPrice = editedFields.additionalPrices?.prices?.[index]
              return editedPrice
                ? { ...price, price: editedPrice.price }
                : price
            }) || [],
        }
      }

      // Filter fields by role:
      // MANAGER -> only basePrice, additionalPrices
      // OWNER/ADMIN -> name, status, bluetoothMacId, basePrice, additionalPrices, lockers
      const filteredPayload: any = { terminalId }

      if (role === FranchiseRole.MANAGER) {
        if (!payload.basePrice && !payload.additionalPrices) {
          setError("Managers can only edit pricing durations and prices.")
          setSaveStatus("error")
          return
        }
        if (payload.basePrice) filteredPayload.basePrice = payload.basePrice
        if (payload.additionalPrices)
          filteredPayload.additionalPrices = payload.additionalPrices
      } else {
        // OWNER or ADMIN
        if (payload.name !== undefined) filteredPayload.name = payload.name
        if (payload.status !== undefined)
          filteredPayload.status = payload.status
        if (payload.bluetoothMacId !== undefined)
          filteredPayload.bluetoothMacId = payload.bluetoothMacId
        if (payload.basePrice) filteredPayload.basePrice = payload.basePrice
        if (payload.additionalPrices)
          filteredPayload.additionalPrices = payload.additionalPrices
        if (payload.lockers) filteredPayload.lockers = payload.lockers
      }

      logger.info(
        "Sending updated terminal data (RBAC-filtered):",
        filteredPayload
      )

      const response = await apiService.updateTerminalDetails(
        role,
        filteredPayload
      )

      if (response?.terminalId) {
        // Update state with the response data
        setTerminalDetails(response)

        // Update session terminal
        const sessionTerminal = {
          terminalId: response.terminalId,
          name: response.name ?? "Unknown",
          address: response.address
            ? `${response.address.street ?? ""}, ${
                response.address.city ?? ""
              }, ${response.address.state ?? ""}`
            : "Unknown",
          status: response.status as Status,
        }

        const updatedTerminals = sessionService.terminals.map((t) =>
          t.terminalId === sessionTerminal.terminalId ? sessionTerminal : t
        )

        sessionService.terminals = updatedTerminals

        setEditMode(false)
        setSaveStatus("success")
        setAlertMessage("Terminal Details edited Successfully!")
        setTimeout(() => setAlertMessage(null), 3000)
        setEditedFields({})
      } else {
        logger.info(
          "No terminal details found in the response. Throwing error..."
        )
        throw new Error(
          response?.message || "No terminal details found in the response."
        )
      }
    } catch (err) {
      logger.error("Error in handleSaveChanges:", err)
      logger.error("Error saving changes:", err)
      setError(err instanceof Error ? err.message : "Error saving changes")
      setSaveStatus("error")
      logger.error("Full error object:", err)
    }
  }

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )

  if (error)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p
            data-automation-id="terminaldetails-error-fetching-details-text"
            id="terminaldetails-error-fetching-details-text"
          >
            {error}
          </p>
        </div>
      </div>
    )

  if (!terminalDetails)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          <p
            data-automation-id="terminaldetails-details-not-avaiable-text"
            id="terminaldetails-details-not-avaiable-text"
          >
            No terminal details found.
          </p>
        </div>
      </div>
    )

  return (
    <div>
      {saveStatus === "saving" && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <p
              data-automation-id="terminaldetails-update-details-saving-text"
              id="terminaldetails-update-details-saving-text"
              className="text-lg font-semibold"
            >
              Saving changes...
            </p>
          </div>
        </div>
      )}

      {saveStatus === "success" && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            {/* Tick Mark in Circle (Green Background, White Tick) */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <svg
                  data-automation-id="terminaldetails-success-message-tickmark-img"
                  id="terminaldetails-success-message-tickmark-img"
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
            </div>

            {/* Success Message (Below Tick Mark) */}
            <p
              data-automation-id="terminaldetails-successful-update-detail-terminal-message-text"
              id="terminaldetails-successful-update-detail-terminal-message-text"
              className="text-lg font-semibold break-words"
            >
              Terminal Details edited <br /> Successfully!
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 flex items-center">
          <button
            data-automation-id="terminaldetails-page-going-back-arrow-button"
            id="terminaldetails-page-going-back-arrow-button"
            onClick={handleClose}
            className="flex items-center text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {editMode ? "Edit Terminal Details" : ""}
          </button>

          {!editMode && canEdit && (
            <button
              data-automation-id="terminaldetails-form-edit-button"
              id="terminaldetails-form-edit-button"
              onClick={handleEditToggle}
              className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-md text-sm"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-1 sm:px-6">
        {!editMode ? (
          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-3 sm:space-y-4">
                {/* Left column */}
                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-32 sm:w-40 text-sm sm:text-base">
                    Name
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.name}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-32 sm:w-40 text-sm sm:text-base">
                    Terminal Id
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.terminalId}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-32 sm:w-40 text-sm sm:text-base">
                    Status
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="xs:ml-2">
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{
                        backgroundColor:
                          terminalDetails.status === Status.Active
                            ? COLORS.ACTIVE
                            : terminalDetails.status === Status.Inactive
                            ? COLORS.INACTIVE
                            : COLORS.MAINTENANCE,
                      }}
                    >
                      {terminalDetails.status}
                    </span>
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-32 sm:w-40 text-sm sm:text-base">
                    Address
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base break-words">
                    {terminalDetails.address?.street}, ,
                    {terminalDetails.address?.city}, ,
                    {terminalDetails.address?.state}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-32 sm:w-40 text-sm sm:text-base">
                    Latitude
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.latitude}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-32 sm:w-40 text-sm sm:text-base">
                    Longitude
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.longitude}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-32 sm:w-40 text-sm sm:text-base">
                    Base Price Duration
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.basePrice?.time} (
                    {terminalDetails.basePrice?.duration})
                  </span>
                </div>

                <div className="pt-2 sm:pt-4">
                  <span className="text-gray-700 font-medium text-sm sm:text-base">
                    Base Price
                  </span>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-2">
                    {terminalDetails.basePrice?.prices.map((price, index) => (
                      <div key={index} className="flex flex-col">
                        <span className="text-xs text-gray-500">
                          {price.size}
                        </span>
                        <span className="font-medium text-sm">
                          Rs. {price.price}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {/* Right column */}
                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-48 text-sm sm:text-base">
                    Allowed Manuals sessions per month
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.allowedManualSessionsPerMonth}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-48 text-sm sm:text-base">
                    Support Number
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.supportNumber}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-48 text-sm sm:text-base">
                    Drop payment required
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.dropPaymentRequired ? "Yes" : "No"}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-48 text-sm sm:text-base">
                    TerminalType
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.terminalType}
                  </span>
                </div>

                <div className="flex flex-col xs:flex-row xs:items-baseline">
                  <span className="text-gray-500 w-full xs:w-48 text-sm sm:text-base">
                    Additional Price Duration
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.additionalPrices?.time} (
                    {terminalDetails.additionalPrices?.duration})
                  </span>
                </div>

                <div className="pt-2 sm:pt-5">
                  <span className="text-gray-700 font-medium text-sm sm:text-base">
                    Additional Price
                  </span>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-2">
                    {terminalDetails.additionalPrices?.prices.map(
                      (price, index) => (
                        <div key={index} className="flex flex-col">
                          <span className="text-xs text-gray-500">
                            {price.size}
                          </span>
                          <span className="font-medium text-sm">
                            Rs. {price.price}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
                {/* Mac ID Display */}
                <div className="flex flex-col xs:flex-row xs:items-baseline mt-3">
                  <span className="text-gray-500 w-full xs:w-48 text-sm sm:text-base">
                    Mac ID
                  </span>
                  <span className="text-gray-900 font-medium hidden xs:inline">
                    :
                  </span>
                  <span className="text-gray-900 xs:ml-2 font-medium xs:font-normal text-sm sm:text-base">
                    {terminalDetails.bluetoothMacId || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Lockers Section */}
            <div className="mt-6 sm:mt-8">
              <h3
                data-automation-id="terminaldetails-form-locker-section-label"
                id="terminaldetails-form-locker-section-label"
                className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4"
              >
                Lockers
              </h3>
              <div className="max-h-80 sm:max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-2 sm:p-3">
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                  {terminalDetails.lockers?.map((locker, index) => (
                    <div
                      key={index}
                      className="border rounded-lg overflow-hidden bg-gray-200 min-h-[120px] sm:aspect-square"
                    >
                      <div className="p-2 sm:p-3 border-b h-full flex flex-col justify-between">
                        {/* Locker Number */}
                        <div>
                          <h4
                            data-automation-id="terminaldetails-locker-details-lockernumber-text"
                            id="terminaldetails-locker-details-lockernumber-text"
                            className="font-bold text-xs sm:text-sm"
                          >
                            Locker {locker.lockerNumber}
                          </h4>

                          {/* Locker Size and Dimensions */}
                          <p className="text-xs text-gray-600 mb-2">
                            <span className="font-bold text-black">
                              {locker.size}
                            </span>
                            <br className="sm:hidden" />
                            <span className="text-gray-500">
                              : {locker.dimensions.width}*
                              {locker.dimensions.height}*
                              {locker.dimensions.depth}
                            </span>
                          </p>
                        </div>

                        {/* Locker Status Badge */}
                        <span
                          className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium text-white w-full"
                          style={{
                            backgroundColor:
                              locker.status.toLowerCase() === "in use"
                                ? COLORS.INACTIVE
                                : locker.status.toLowerCase() === "available"
                                ? COLORS.ACTIVE
                                : locker.status.toLowerCase() === "maintenance"
                                ? COLORS.MAINTENANCE
                                : COLORS.DEFAULT,
                          }}
                        >
                          {locker.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
            {editMode ? (
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
                {isOwnerOrAdmin && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <label
                        data-automation-id="terminaldetails-form-name-label"
                        id="terminaldetails-form-name-label"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Name
                      </label>
                      <input
                        data-automation-id="terminaldetails-form-inputbox-name-input"
                        id="terminaldetails-form-inputbox-name-input"
                        type={TYPES.TEXT}
                        value={editedFields.name ?? terminalDetails.name ?? ""}
                        onChange={(e) => handleChange("name", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-200 text-sm sm:text-base"
                      />
                    </div>

                    <div>
                      <label
                        data-automation-id="terminaldetails-form-status-label"
                        id="terminaldetails-form-status-label"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Status
                      </label>
                      <select
                        data-automation-id="terminaldetails-form-dropdown-status-select"
                        id="terminaldetails-form-dropdown-status-select"
                        value={editedFields.status || terminalDetails.status}
                        onChange={(e) => handleChange("status", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-200 text-sm sm:text-base"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Maintenance">Maintenance</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Mac ID only OWNER/ADMIN */}
                {isOwnerOrAdmin && (
                  <div>
                    <label
                      data-automation-id="terminaldetails-form-macid-label"
                      id="terminaldetails-form-macid-label"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Mac ID
                    </label>
                    <input
                      data-automation-id="terminaldetails-form-inputbox-macid-input"
                      id="terminaldetails-form-inputbox-macid-input"
                      type="text"
                      value={
                        editedFields.bluetoothMacId ??
                        terminalDetails.bluetoothMacId ??
                        ""
                      }
                      onChange={(e) =>
                        handleChange("bluetoothMacId", e.target.value)
                      }
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-200 text-sm sm:text-base"
                    />
                  </div>
                )}

                {/* Durations visible for MANAGER and OWNER/ADMIN */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base Price Duration
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        placeholder="Time value"
                        value={
                          editedFields.basePrice?.time ??
                          terminalDetails.basePrice?.time ??
                          ""
                        }
                        onChange={(e) =>
                          handleNestedChange(
                            "basePrice",
                            "time",
                            Math.max(1, Number.parseInt(e.target.value))
                          )
                        }
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-200 text-sm sm:text-base"
                      />
                      <select
                        value={
                          editedFields.basePrice?.duration ??
                          terminalDetails.basePrice?.duration ??
                          ""
                        }
                        onChange={(e) =>
                          handleNestedChange(
                            "basePrice",
                            "duration",
                            e.target.value
                          )
                        }
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-200 text-sm sm:text-base"
                      >
                        <option value="">Select unit</option>
                        {Object.values(Duration).map((duration) => (
                          <option key={duration} value={duration}>
                            {duration.charAt(0).toUpperCase() +
                              duration.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Price Duration
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        placeholder="Time value"
                        value={
                          editedFields.additionalPrices?.time ??
                          terminalDetails.additionalPrices?.time ??
                          ""
                        }
                        onChange={(e) =>
                          handleNestedChange(
                            "additionalPrices",
                            "time",
                            Math.max(1, Number.parseInt(e.target.value))
                          )
                        }
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-200 text-sm sm:text-base"
                      />
                      <select
                        value={
                          editedFields.additionalPrices?.duration ??
                          terminalDetails.additionalPrices?.duration ??
                          ""
                        }
                        onChange={(e) =>
                          handleNestedChange(
                            "additionalPrices",
                            "duration",
                            e.target.value
                          )
                        }
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-200 text-sm sm:text-base"
                      >
                        <option value="">Select unit</option>
                        {Object.values(Duration).map((duration) => (
                          <option key={duration} value={duration}>
                            {duration.charAt(0).toUpperCase() +
                              duration.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Base/Additional Price visible for MANAGER and OWNER/ADMIN */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  {/* Base Price Section */}
                  <div>
                    <h3
                      data-automation-id="terminaldetails-form-baseprice-section-text"
                      id="terminaldetails-form-baseprice-section-text"
                      className="text-sm font-medium text-gray-700 mb-2"
                    >
                      Base Price (in rupees)
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {terminalDetails.basePrice?.prices.map((price, index) => (
                        <div key={index} className="flex-1">
                          <label
                            data-automation-id="terminaldetails-form-baseprice-label"
                            id="terminaldetails-form-baseprice-label"
                            className="block text-xs font-medium text-gray-500 mb-1"
                          >
                            {price.size}
                          </label>
                          <input
                            data-automation-id="terminaldetails-form-inputbox-baseprice-input"
                            id="terminaldetails-form-inputbox-baseprice-input"
                            type={TYPES.NUMBER}
                            min="0"
                            value={
                              editedFields.basePrice?.prices?.[index]?.price ??
                              price.price ??
                              ""
                            }
                            onChange={(e) => {
                              const newValue = Number.parseFloat(e.target.value)
                              if (newValue < 0 || isNaN(newValue)) return // prevent negative or invalid values
                              handleNestedChange(
                                "basePrice",
                                "prices",
                                { ...price, price: newValue },
                                index
                              )
                            }}
                            className="w-full p-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-200"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Price Section */}
                  <div>
                    <h3
                      data-automation-id="terminaldetails-form-additional-price-section-text"
                      id="terminaldetails-form-additional-price-section-text"
                      className="text-sm font-medium text-gray-700 mb-2"
                    >
                      Additional Price (in rupees)
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {terminalDetails.additionalPrices?.prices.map(
                        (price, index) => (
                          <div key={index} className="flex-1">
                            <label
                              data-automation-id="terminaldetails-form-additionalprice-label"
                              id="terminaldetails-form-additionalprice-label"
                              className="block text-xs font-medium text-gray-500 mb-1"
                            >
                              {price.size}
                            </label>
                            <input
                              data-automation-id="terminaldetails-form-inputbox-additionalprice-input"
                              id="terminaldetails-form-inputbox-additionalprice-input"
                              type={TYPES.NUMBER}
                              min="0"
                              value={
                                editedFields.additionalPrices?.prices?.[index]
                                  ?.price ??
                                price.price ??
                                ""
                              }
                              onChange={(e) => {
                                const newValue = Number.parseFloat(
                                  e.target.value
                                )
                                if (newValue < 0 || isNaN(newValue)) return // prevent negative or invalid values
                                handleNestedChange(
                                  "additionalPrices",
                                  "prices",
                                  { ...price, price: newValue },
                                  index
                                )
                              }}
                              className="w-full p-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-200"
                            />
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Lockers only OWNER/ADMIN */}
                {isOwnerOrAdmin && (
                  <div className="mb-4 sm:mb-6">
                    <h3
                      data-automation-id="terminaldetails-form-locker-section-text"
                      id="terminaldetails-form-locker-section-text"
                      className="text-sm font-medium text-gray-700 mb-2"
                    >
                      Lockers
                    </h3>
                    <div className="max-h-72 xs:max-h-80 sm:max-h-96 overflow-y-auto border border-gray-300 rounded-lg p-2 sm:p-3 bg-gray-50">
                      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                        {terminalDetails.lockers?.map((locker, index) => (
                          <div
                            key={index}
                            className="border rounded-lg overflow-hidden bg-gray-200 min-h-[100px] xs:min-h-[120px] sm:aspect-square"
                          >
                            <div className="p-2 sm:p-3 border-b h-full flex flex-col justify-between">
                              <div>
                                <h4
                                  data-automation-id="terminaldetails-locker-details-locker-dimension-text"
                                  id="terminaldetails-locker-details-locker-dimension-text"
                                  className="font-medium text-xs sm:text-sm mb-1"
                                >
                                  Locker {locker.lockerNumber}
                                </h4>
                                <p className="text-xs text-gray-500 mb-2 break-words">
                                  {locker.size}: {locker.dimensions.width}*
                                  {locker.dimensions.height}*
                                  {locker.dimensions.depth}
                                </p>
                              </div>
                              <select
                                data-automation-id="terminaldetails-form-dropdown-lockerstatus-select"
                                id="terminaldetails-form-dropdown-lockerstatus-select"
                                value={
                                  editedFields.lockers?.[index]?.status ||
                                  locker.status
                                }
                                onChange={(e) =>
                                  handleLockerStatusChange(
                                    index,
                                    e.target.value
                                  )
                                }
                                className="w-full text-xs sm:text-sm p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                              >
                                <option value="Available">Available</option>
                                <option value="In Use">In Use</option>
                                <option value="Maintenance">Maintenance</option>
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6">
                  <button
                    data-automation-id="terminaldetails-close-form-button"
                    id="terminaldetails-close-form-button"
                    onClick={handleEditToggle}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    data-automation-id="terminaldetails-save-updates-details-button"
                    id="terminaldetails-save-updates-details-button"
                    onClick={handleSaveChanges}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
                {/* Display non-editable fields here */}
                {/* Base Price Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <h3
                      data-automation-id="terminaldetails-form-baseprice-section-text"
                      id="terminaldetails-form-baseprice-section-text"
                      className="text-sm font-medium text-gray-700 mb-2"
                    >
                      Base Price (in rupees)
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {terminalDetails.basePrice?.prices.map((price, index) => (
                        <div key={index} className="flex flex-col">
                          <span className="text-xs text-gray-500">
                            {price.size}
                          </span>
                          <span className="font-medium text-sm">
                            Rs. {price.price}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Price Section */}
                  <div>
                    <h3
                      data-automation-id="terminaldetails-form-additional-price-section-text"
                      id="terminaldetails-form-additional-price-section-text"
                      className="text-sm font-medium text-gray-700 mb-2"
                    >
                      Additional Price (in rupees)
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {terminalDetails.additionalPrices?.prices.map(
                        (price, index) => (
                          <div key={index} className="flex flex-col">
                            <span className="text-xs text-gray-500">
                              {price.size}
                            </span>
                            <span className="font-medium text-sm">
                              Rs. {price.price}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Lockers Section */}
                <div className="mb-4 sm:mb-6">
                  <h3
                    data-automation-id="terminaldetails-form-locker-section-text"
                    id="terminaldetails-form-locker-section-text"
                    className="text-sm font-medium text-gray-700 mb-2"
                  >
                    Lockers
                  </h3>
                  <div className="max-h-72 xs:max-h-80 sm:max-h-96 overflow-y-auto border border-gray-300 rounded-lg p-2 sm:p-3 bg-gray-50">
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                      {terminalDetails.lockers?.map((locker, index) => (
                        <div
                          key={index}
                          className="border rounded-lg overflow-hidden bg-gray-200 min-h-[100px] xs:min-h-[120px] sm:aspect-square"
                        >
                          <div className="p-2 sm:p-3 border-b h-full flex flex-col justify-between">
                            <div>
                              <h4
                                data-automation-id="terminaldetails-locker-details-locker-dimension-text"
                                id="terminaldetails-locker-details-locker-dimension-text"
                                className="font-medium text-xs sm:text-sm mb-1"
                              >
                                Locker {locker.lockerNumber}
                              </h4>
                              <p className="text-xs text-gray-500 mb-2 break-words">
                                {locker.size}: {locker.dimensions.width}*
                                {locker.dimensions.height}*
                                {locker.dimensions.depth}
                              </p>
                            </div>
                            <span
                              className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium text-white w-full"
                              style={{
                                backgroundColor:
                                  locker.status.toLowerCase() === "in use"
                                    ? COLORS.INACTIVE
                                    : locker.status.toLowerCase() ===
                                      "available"
                                    ? COLORS.ACTIVE
                                    : locker.status.toLowerCase() ===
                                      "maintenance"
                                    ? COLORS.MAINTENANCE
                                    : COLORS.DEFAULT,
                              }}
                            >
                              {locker.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {alertMessage && (
          <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {alertMessage}
          </div>
        )}
      </div>
    </div>
  )
}

export default TerminalDetails